"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState, useEffect } from "react";
import { Quote, Star, CircleCheck } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, type CarouselApi } from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
const testimonials = [{
  id: 1,
  author: "Robert M.",
  role: "Patient",
  quote: "\"Paul helped me understand my Gleason score and MRI findings when I felt completely overwhelmed. I went from panic to having a clear, confident plan for active surveillance. His guidance was truly a lifeline.\"",
  savings: "Clarity Gained",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1605152/img/testimonial_robert_avatar.png",
  rating: 5
}, {
  id: 2,
  author: "Susan K.",
  role: "Caregiver & Spouse",
  quote: "\"My husband's diagnosis was a shock. Paul helped explain the quality-of-life impacts of radiation versus surgery and this helped us make a decision that aligned with our personal goals. We weren't alone in this.\"",
  savings: "Peace of Mind",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1605152/img/testimonial_susan_avatar.png",
  rating: 5
}, {
  id: 3,
  author: "David T.",
  role: "Patient",
  quote: "I was confused by conflicting advice from different providers. The detailed review of my genomic testing results finally gave me the confidence to move forward with focal therapy. This service is essential for any patient.",
  savings: "Informed Decision",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1605152/img/testimonial_david_avatar.png",
  rating: 5
}];
export default function RealSuccessStories() {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!api) {
      return;
    }
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap());
    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);
  const handleDotClick = (index: number) => {
    if (api) {
      api.scrollTo(index);
    }
  };
  return <section data-section-id="67212" id="voices-of-clarity-and-hope" className="bg-background text-foreground py-16 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-96 h-96 bg-[#111c2d]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] left-[-5%] w-96 h-96 bg-[#5c8a73]/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-10 max-w-3xl mx-auto">
          <Badge variant="outline" className="mb-4 border-[#111c2d]/20 text-[#111c2d] bg-[#111c2d]/5 px-4 py-1 text-sm uppercase tracking-wider font-sans">Personal Experiences</Badge>
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-[#111c2d] mb-6 tracking-tight">
            Voices of Clarity and Hope
          </h2>
          <p className="text-lg text-muted-foreground font-sans">Hear from individuals and families who found confidence and peace of mind navigating their prostate cancer journey with my personalized support.</p>
        </div>

        <div className="max-w-5xl mx-auto">
          <Carousel setApi={setApi} opts={{
          align: "center",
          loop: true
        }} className="w-full">
            <CarouselContent className="-ml-4">
              {testimonials.map((testimonial, index) => <CarouselItem key={testimonial.id} className="pl-4 md:basis-full lg:basis-full">
                  <div className="p-2" data-index={index}>
                    <Card className="border-none shadow-xl bg-card/80 backdrop-blur-sm overflow-hidden relative rounded-3xl">
                      <CardContent className="p-8 md:p-12 flex flex-col md:flex-row gap-8 items-center md:items-start">
                        {}
                        <div className="absolute top-6 right-6 md:top-8 md:right-8 z-20">
                          <Badge className="bg-[#5c8a73] text-white px-4 py-2 text-sm font-bold shadow-lg flex items-center gap-2 rounded-full font-sans">
                            <CircleCheck className="w-4 h-4" />
                            {testimonial.savings}
                          </Badge>
                        </div>

                        {}
                        <div className="flex-shrink-0 relative group">
                          <SafeHydrate><Avatar className="w-24 h-24 md:w-32 md:h-32 border-4 border-[#5c8a73] shadow-lg relative z-10">
                            <AvatarFallback className="bg-muted text-2xl font-serif text-[#111c2d]">
                              {testimonial.author.charAt(0)}
                            </AvatarFallback>
                          </Avatar></SafeHydrate>
                          <div className="flex justify-center mt-4 gap-1 text-[#5c8a73]">
                            {[...Array(testimonial.rating)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" />)}
                          </div>
                        </div>

                        {}
                        <div className="flex-1 text-center md:text-left pt-4 md:pt-2">
                          <Quote className="w-10 h-10 text-[#111c2d]/10 mb-4 mx-auto md:mx-0" />
                          <blockquote className="text-xl md:text-2xl font-serif italic text-[#111c2d] leading-relaxed mb-6">
                            "{testimonial.quote}"
                          </blockquote>
                          <div>
                            <cite className="not-italic font-bold text-lg text-[#111c2d] block font-sans">
                              {testimonial.author}
                            </cite>
                            <span className="text-muted-foreground text-sm font-medium font-sans">
                              {testimonial.role}
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </CarouselItem>)}
            </CarouselContent>

            {}
            <div className="hidden md:block">
              <CarouselPrevious className="left-[-3rem] w-12 h-12 border-[#111c2d]/20 text-[#111c2d] hover:bg-[#111c2d] hover:text-white hover:border-[#111c2d] transition-colors rounded-full" />
              <CarouselNext className="right-[-3rem] w-12 h-12 border-[#111c2d]/20 text-[#111c2d] hover:bg-[#111c2d] hover:text-white hover:border-[#111c2d] transition-colors rounded-full" />
            </div>
          </Carousel>

          {}
          <div className="flex justify-center gap-3 mt-6">
            {Array.from({
            length: count
          }).map((_, index) => <Button key={index} variant="ghost" size="sm" className={cn("w-3 h-3 p-0 rounded-full transition-all duration-300 hover:bg-[#3350cc]/50", current === index ? "bg-[#3350cc] w-8" : "bg-muted-foreground/30")} onClick={() => handleDotClick(index)} aria-label={`Go to slide ${index + 1}`}>
                <span className="sr-only">Slide {index + 1}</span>
              </Button>)}
          </div>
        </div>
      </div>
    </section>;
}